import { ApiError, api } from '../../lib/api'
import { isEnglish } from '../../lib/i18n'

/**
 * Programme reports — `Features/Admin/LearningProgramsReports/Endpoint.cs`,
 * `ContentManagers`.
 *
 *   getReportsPage    -> GET  /api/admin/programs-reports/         the programme list
 *   getProgramSummary -> POST /LearningProgramsReports/GetProgramDetails  { Id, From, To }
 *
 * FIVE MORE ENDPOINTS ARE NOT HERE, AND CANNOT BE. `GetSucceeded`, `GetFailed`,
 * `GetUnattended`, `GetUncompleted` and `GetGroupsLevel` take no programme id:
 *
 *     var programId = context.Session.GetInt32("_SelectedProgramId") ?? 0;
 *
 * The legacy controller SET that session value in its page action
 * (`LearningProgramsReportsController.cs:139`) and the port copied the reads without the
 * write — `_SelectedProgramId` is read five times in this solution and written zero times.
 * So all five answer for programme 0 whoever asks, from any client, today. A browser cannot
 * set server session state, so this is not something to adapt around. See
 * `API_FINDINGS.md` §37.
 *
 * `GetProgramDetails` takes its id in the body, which is why the summary works and the four
 * drill-down tables do not.
 */

const BASE = '/LearningProgramsReports'

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** The programmes a report can be run for. */
export async function getReportsPage(signal) {
  const dto = (await api.get('/api/admin/programs-reports/', { signal })) ?? {}

  return {
    programs: (dto.Programs ?? []).map((program) => ({
      value: String(program.Id),
      label: label(program.NameArabic, program.NameEnglish) || program.Name || '',
    })),
  }
}

/**
 * One programme's outcome, optionally within a date range.
 *
 * These four percentages are the same figures the programmes list draws its dials from —
 * per programme, which is the only granularity any endpoint offers. Nothing aggregates them
 * across programmes, which is why that strip stays hidden (`COSMETIC_UI.md`).
 */
export async function getProgramSummary({ programId, fromDate, toDate, signal } = {}) {
  const dto = await api.post(
    `${BASE}/GetProgramDetails`,
    { Id: Number(programId), From: fromDate || null, To: toDate || null },
    { signal },
  )

  if (!dto) return null

  return {
    title: dto.ProgramTitle ?? '',
    startDate: dto.ProgramStartDate ?? '',
    endDate: dto.ProgramEndDate ?? '',
    coursesCount: dto.CoursesCount ?? 0,
    groupsCount: dto.GroupsCount ?? 0,
    usersCount: dto.UsersCount ?? 0,
    outcome: {
      success: Number(dto.SuccessPercentage ?? 0),
      failure: Number(dto.FailurePercentage ?? 0),
      incomplete: Number(dto.UncompletedPercentage ?? 0),
      absent: Number(dto.UnattendedPercentage ?? 0),
    },
    counts: {
      success: dto.SuccessCount ?? 0,
      failure: dto.FailureCount ?? 0,
      incomplete: dto.UncompletedCount ?? 0,
      absent: dto.UnattendedCount ?? 0,
    },
    courses: (dto.CoursesData ?? []).map((course) => ({
      id: course.Id,
      name: course.Name ?? '',
      startDate: course.StartDate ?? '',
      endDate: course.EndDate ?? '',
      topicsCount: course.CategoriesCount ?? 0,
      success: course.SuccessCount ?? 0,
      failure: course.FailureCount ?? 0,
      absent: course.AbsenceCount ?? 0,
    })),
    /* One row per (question, answer) with how many chose it — not a per-user list. */
    survey: (dto.SurveyData ?? []).map((row, index) => ({
      id: `${row.Question}|${row.Answer}|${index}`,
      question: row.Question ?? '',
      answer: row.Answer ?? '',
      count: row.Count ?? '',
    })),
  }
}

/** Kept for the caller's error handling; the summary is the only call that can fail here. */
export async function tryGetProgramSummary(args) {
  try {
    return { ok: true, summary: await getProgramSummary(args) }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, summary: null }
    throw error
  }
}
